// LOCKED - DO NOT MODIFY unless specifically directed by user.
import React, { useState, useRef, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Loader2 } from 'lucide-react';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Configure PDF.js worker using CDN for compatibility
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export default function PDFViewer({ 
  file, 
  title = 'Document',
  maxHeight = '600px',
  showControls = true,
  className = ''
}) {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [containerWidth, setContainerWidth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const measure = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth - 32);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
    setPageNumber(1);
    setLoading(false);
    setError(null);
  }

  function onDocumentLoadError(err) {
    console.error('PDF load error:', err);
    setLoading(false);
    setError(err.message || 'Failed to load PDF');
  }

  function changePage(offset) {
    setPageNumber(prevPage => Math.min(Math.max(1, prevPage + offset), numPages || 1));
  }

  function changeZoom(delta) {
    setScale(prevScale => Math.min(Math.max(0.5, prevScale + delta), 2.5));
  }

  if (!file) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 p-8 ${className}`} style={{ minHeight: maxHeight }}>
        <p className="text-gray-400 italic">No document available</p>
      </div>
    );
  }

  const pageWidth = containerWidth ? containerWidth * scale : undefined;

  return (
    <div className={`flex flex-col ${className}`}>
      {showControls && (
        <div className="flex items-center justify-between bg-gray-100 px-4 py-2 border-b" data-testid="pdf-controls">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => changePage(-1)} disabled={pageNumber <= 1 || loading} className="h-8 w-8 p-0" data-testid="pdf-prev-page">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-gray-600 min-w-[100px] text-center" data-testid="pdf-page-info">
              {loading ? 'Loading...' : `Page ${pageNumber} of ${numPages || '?'}`}
            </span>
            <Button variant="outline" size="sm" onClick={() => changePage(1)} disabled={pageNumber >= (numPages || 1) || loading} className="h-8 w-8 p-0" data-testid="pdf-next-page">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => changeZoom(-0.2)} disabled={scale <= 0.5 || loading} className="h-8 w-8 p-0" data-testid="pdf-zoom-out">
              <ZoomOut className="h-4 w-4" />
            </Button>
            <span className="text-sm text-gray-600 min-w-[50px] text-center" data-testid="pdf-zoom-level">
              {Math.round(scale * 100)}%
            </span>
            <Button variant="outline" size="sm" onClick={() => changeZoom(0.2)} disabled={scale >= 2.5 || loading} className="h-8 w-8 p-0" data-testid="pdf-zoom-in">
              <ZoomIn className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <div 
        ref={containerRef}
        className="overflow-auto bg-gray-200" 
        style={{ maxHeight, minHeight: '200px' }}
        data-testid="pdf-container"
      >
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2 text-gray-600">Loading PDF...</span>
          </div>
        )}
        
        {error && (
          <div className="flex items-center justify-center py-12 text-red-500">
            <p>Error loading PDF: {error}</p>
          </div>
        )}

        <Document
          file={file}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading=""
          error=""
          className="flex justify-center py-4"
        >
          <Page 
            pageNumber={pageNumber} 
            width={pageWidth}
            renderTextLayer={false}
            renderAnnotationLayer={true}
            className="shadow-lg"
          />
        </Document>
      </div>
    </div>
  );
}

// Simple PDF iframe fallback for non-PDF files or when react-pdf fails
export function PDFIframe({ src, title = 'Document', height = '600px', className = '' }) {
  if (!src) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 p-8 ${className}`} style={{ height }}>
        <p className="text-gray-400 italic">No document available</p>
      </div>
    );
  }

  return (
    <iframe
      src={src}
      className={`w-full bg-white ${className}`}
      style={{ height }}
      title={title}
    />
  );
}
